/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * WORKFLOWEXECUTIONSERVICE - EXECUÇÃO PROGRAMÁTICA DE WORKFLOWS
 * 
 * Serviço simplificado para executar workflows programaticamente
 * sem UI interativa (usado durante instalação de agentes)
 */

import { post, engineApiBase, engineEventsUrl } from '@/shared/utils/fetchApi';
import { SSEConnection } from '@/shared/utils/sseConnection';

export interface WorkflowExecutionPayload {
  nodes: any[];
  edges: any[];
  prompts: any[];
  parameters: Record<string, any>;
  agentBaseId: string;
  organizationId: string;
  workspaceId: string;
}

export interface WorkflowExecutionResult {
  executionId: string;
  status: 'success' | 'error' | 'timeout';
  error?: string;
  results?: any;
}

export class WorkflowExecutionService {
  /**
   * Executa um workflow e aguarda sua conclusão
   * 
   * @param payload Payload do workflow a ser executado
   * @param timeoutMs Tempo máximo de espera em ms (padrão: 5 minutos)
   * @returns Resultado da execução
   */
  static async executeAndWait(
    payload: WorkflowExecutionPayload,
    timeoutMs: number = 300000
  ): Promise<WorkflowExecutionResult> {
    try {
      // 1. Iniciar execução do workflow
      const result = await post<{ executionId?: string }>(`${engineApiBase()}/workflow/execute`, payload);

      const executionId = result.executionId;
      
      if (!executionId) {
        throw new Error('ID de execução não retornado pelo servidor');
      }

      // 2. Monitorar execução via SSE
      return await this.waitForCompletion(executionId, timeoutMs);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      return {
        executionId: '',
        status: 'error',
        error: errorMessage
      };
    }
  }

  /**
   * Aguarda a conclusão de uma execução de workflow via SSE
   */
  private static async waitForCompletion(
    executionId: string,
    timeoutMs: number
  ): Promise<WorkflowExecutionResult> {
    return new Promise((resolve) => {
      const connection = new SSEConnection({
        url: engineEventsUrl(executionId),
        // Workflow monitor precisa manter a conexão até  completar, então usamos número alto
        maxRetries: 10,
        initialRetryDelay: 1000,
        maxRetryDelay: 16000
      });

      const eventSource = connection.connect();
      
      let completed = false;

      // Timeout de segurança
      const timeoutId: NodeJS.Timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          eventSource.close();
          resolve({
            executionId,
            status: 'timeout',
            error: 'Tempo limite de execução excedido'
          });
        }
      }, timeoutMs);
      
      // Handler de eventos de status
      eventSource.addEventListener('status', (event) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          // Verificar se o workflow foi concluído
          if (data.status === 'completed') {
            if (!completed) {
              completed = true;
              clearTimeout(timeoutId);
              eventSource.close();
              resolve({
                executionId,
                status: 'success',
                results: data
              });
            }
          } else if (data.status === 'error') {
            if (!completed) {
              completed = true;
              clearTimeout(timeoutId);
              eventSource.close();
              resolve({
                executionId,
                status: 'error',
                error: data.error || 'Erro na execução do workflow',
                results: data
              });
            }
          }
        } catch (error) {
          // Ignorar erros de parsing
        }
      });
      
      // Handler de erros de conexão
      eventSource.onerror = () => {
        if (!completed) {
          completed = true;
          clearTimeout(timeoutId);
          eventSource.close();
          resolve({
            executionId,
            status: 'error',
            error: 'Erro na conexão SSE'
          });
        }
      };
    });
  }
}
